import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FileText, Plus, ArrowLeft, Edit, Trash2, Search, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const INSURANCE_TYPES = [
  { value: 'auto', label: 'Automobile' },
  { value: 'habitation', label: 'Habitation' },
  { value: 'sante', label: 'Santé' },
  { value: 'prevoyance', label: 'Prévoyance' },
  { value: 'rc_pro', label: 'RC Professionnelle' },
  { value: 'multirisque_pro', label: 'Multirisque Pro' },
  { value: 'decennale', label: 'Garantie Décennale' },
  { value: 'flotte', label: 'Flotte Auto' },
  { value: 'autre', label: 'Autre' }
];

const PAYMENT_FREQUENCIES = [
  { value: 'unique', label: 'Unique' },
  { value: 'mensuel', label: 'Mensuel' },
  { value: 'trimestriel', label: 'Trimestriel' },
  { value: 'semestriel', label: 'Semestriel' },
  { value: 'annuel', label: 'Annuel' }
];

export const AdminContracts = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [apporteurs, setApporteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterApporteur, setFilterApporteur] = useState('all');
  const [formData, setFormData] = useState({
    apporteur_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    insurance_type: '',
    insurance_company: '',
    contract_number: '',
    premium_amount: 0,
    broker_commission: 0,
    payment_frequency: 'annuel',
    start_date: '',
    end_date: '',
    effect_date: '',
    effect_date_na: false, // For old contracts without effect date
    status: 'actif',
    notes: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAdmin, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [contractsRes, apporteursRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/contracts`),
        axios.get(`${API_URL}/api/admin/apporteurs`)
      ]);
      setContracts(contractsRes.data);
      setApporteurs(apporteursRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        premium_amount: parseFloat(formData.premium_amount),
        broker_commission: parseFloat(formData.broker_commission),
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        effect_date: formData.effect_date_na ? null : (formData.effect_date ? new Date(formData.effect_date).toISOString() : null),
        effect_date_na: formData.effect_date_na
      };
      
      // Remove effect_date_na from data as it's only for UI
      delete data.effect_date_na;

      if (editingId) {
        const { apporteur_id, ...updateData } = data;
        await axios.put(`${API_URL}/api/admin/contracts/${editingId}`, updateData);
        toast.success('Contrat modifié');
      } else {
        await axios.post(`${API_URL}/api/admin/contracts`, data);
        toast.success('Contrat créé');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (contract) => {
    // Check if effect_date is null (N/A case)
    const hasEffectDate = contract.effect_date && contract.effect_date !== 'N/A';
    
    setFormData({
      apporteur_id: contract.apporteur_id,
      client_name: contract.client_name,
      client_email: contract.client_email || '',
      client_phone: contract.client_phone || '',
      insurance_type: contract.insurance_type,
      insurance_company: contract.insurance_company,
      contract_number: contract.contract_number || '',
      premium_amount: contract.premium_amount,
      broker_commission: contract.broker_commission,
      payment_frequency: contract.payment_frequency || 'annuel',
      start_date: contract.start_date?.split('T')[0] || '',
      end_date: contract.end_date?.split('T')[0] || '',
      effect_date: hasEffectDate ? contract.effect_date?.split('T')[0] : '',
      effect_date_na: !hasEffectDate && contract.effect_date_na === true,
      status: contract.status,
      notes: contract.notes || ''
    });
    setEditingId(contract.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce contrat ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/contracts/${id}`);
      toast.success('Contrat supprimé');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      apporteur_id: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      insurance_type: '',
      insurance_company: '',
      contract_number: '',
      premium_amount: 0,
      broker_commission: 0,
      payment_frequency: 'annuel',
      start_date: '',
      end_date: '',
      effect_date: '',
      effect_date_na: false,
      status: 'actif',
      notes: ''
    });
  };

  const filteredContracts = contracts.filter(c => {
    const matchesSearch = c.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.insurance_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesApporteur = filterApporteur === 'all' || c.apporteur_id === filterApporteur;
    return matchesSearch && matchesApporteur;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestion Contrats | Admin CAMBC</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-slate-100 pt-20 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft size={24} />
              </Link>
              <h1 className="text-2xl font-bold text-slate-900">Gestion des Contrats</h1>
            </div>
            <Button onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }}>
              <Plus size={18} className="mr-2" /> Nouveau Contrat
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterApporteur} onValueChange={setFilterApporteur}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrer par apporteur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les apporteurs</SelectItem>
                {apporteurs.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <Card className="w-full max-w-2xl my-8">
                <CardHeader>
                  <CardTitle>{editingId ? 'Modifier Contrat' : 'Nouveau Contrat'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Apporteur *</Label>
                        <Select 
                          value={formData.apporteur_id} 
                          onValueChange={(v) => setFormData({...formData, apporteur_id: v})}
                          disabled={!!editingId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {apporteurs.filter(a => a.is_active).map(a => (
                              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Type d'assurance *</Label>
                        <Select 
                          value={formData.insurance_type} 
                          onValueChange={(v) => setFormData({...formData, insurance_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {INSURANCE_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nom du client *</Label>
                        <Input
                          value={formData.client_name}
                          onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Email client</Label>
                        <Input
                          type="email"
                          value={formData.client_email}
                          onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Téléphone client</Label>
                        <Input
                          value={formData.client_phone}
                          onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Compagnie d'assurance *</Label>
                        <Input
                          value={formData.insurance_company}
                          onChange={(e) => setFormData({...formData, insurance_company: e.target.value})}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label>N° Contrat</Label>
                        <Input
                          value={formData.contract_number}
                          onChange={(e) => setFormData({...formData, contract_number: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Prime annuelle (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.premium_amount}
                          onChange={(e) => setFormData({...formData, premium_amount: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Commission courtier (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.broker_commission}
                          onChange={(e) => setFormData({...formData, broker_commission: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Fractionnement *</Label>
                        <Select 
                          value={formData.payment_frequency} 
                          onValueChange={(v) => setFormData({...formData, payment_frequency: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_FREQUENCIES.map(f => (
                              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Date de souscription *</Label>
                        <Input
                          type="date"
                          value={formData.start_date}
                          onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Date de fin</Label>
                        <Input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                        />
                      </div>
                    </div>

                    {/* Date d'effet avec option N/A */}
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-amber-800 font-semibold">Date d'effet (pour suivi commissions)</Label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.effect_date_na}
                            onChange={(e) => setFormData({
                              ...formData, 
                              effect_date_na: e.target.checked,
                              effect_date: e.target.checked ? '' : formData.effect_date
                            })}
                            className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm text-amber-700">N/A (ancien logiciel)</span>
                        </label>
                      </div>
                      {!formData.effect_date_na ? (
                        <Input
                          type="date"
                          value={formData.effect_date}
                          onChange={(e) => setFormData({...formData, effect_date: e.target.value})}
                          className="bg-white"
                        />
                      ) : (
                        <div className="p-3 bg-amber-100 rounded text-amber-800 text-sm">
                          ℹ️ Ce contrat provient d'un ancien système. La date d'effet n'est pas définie et ne sera pas prise en compte dans le suivi des commissions.
                        </div>
                      )}
                      <p className="text-xs text-amber-600 mt-2">
                        Cette date est utilisée pour le suivi des rétrocessions. Les mois avant cette date seront marqués "N/A".
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label>Statut</Label>
                        <Select 
                          value={formData.status} 
                          onValueChange={(v) => setFormData({...formData, status: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="actif">Actif</SelectItem>
                            <SelectItem value="en_attente">En attente</SelectItem>
                            <SelectItem value="resilie">Résilié</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        rows={3}
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1">
                        {editingId ? 'Modifier' : 'Créer'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                        Annuler
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Contracts Table */}
          <Card className="bg-white shadow-lg border-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Client</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Type</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Apporteur</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Date Effet</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Prime</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Comm. Apporteur</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Statut</th>
                    <th className="text-right p-4 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredContracts.length > 0 ? (
                    filteredContracts.map((contract) => (
                      <tr key={contract.id} className="hover:bg-slate-50">
                        <td className="p-4">
                          <p className="font-medium text-slate-900">{contract.client_name}</p>
                          <p className="text-sm text-slate-500">{contract.insurance_company}</p>
                        </td>
                        <td className="p-4 text-slate-600">
                          {INSURANCE_TYPES.find(t => t.value === contract.insurance_type)?.label || contract.insurance_type}
                        </td>
                        <td className="p-4 text-slate-600">{contract.apporteur_name}</td>
                        <td className="p-4">
                          {contract.effect_date_na ? (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-500">N/A</span>
                          ) : contract.effect_date ? (
                            <span className="text-slate-600">{new Date(contract.effect_date).toLocaleDateString('fr-FR')}</span>
                          ) : (
                            <span className="text-slate-400">Non définie</span>
                          )}
                        </td>
                        <td className="p-4 text-slate-600">{contract.premium_amount?.toLocaleString('fr-FR')} €</td>
                        <td className="p-4 font-medium text-blue-600">{contract.apporteur_commission?.toLocaleString('fr-FR')} €</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            contract.status === 'actif' ? 'bg-green-100 text-green-700' :
                            contract.status === 'en_attente' ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {contract.status === 'actif' ? 'Actif' : 
                             contract.status === 'en_attente' ? 'En attente' : 'Résilié'}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(contract)}>
                            <Edit size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(contract.id)}>
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500">
                        <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                        <p>Aucun contrat trouvé</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};
